import styled from 'styled-components';
import {useRef, useEffect, useState} from 'react';
import {Tag} from '@blueprintjs/core';

import {StatelemsRenderer} from './statelems-renderer';
import {Curve, Area} from './elems'

const CameraCanvas = styled.canvas`
    width: 100%;
`;

const Taxonomies = styled.div`
    position: absolute;
    display: none;
    padding: 8px 12px;
    background-color: #1f2937;
    color: white;
    border-radius: 6px;
    font-size: 14px;
    pointer-events: none;
    transform: translate(10px, -30px);
`;

const TaxonomyTag = (
    {
        highlight: initHighlight = false,
        onMouseEnter,
        onMouseLeave,
        children,
        ...props
    }) => {
    const [highlight, setHighlight] = useState(initHighlight);

    useEffect(() => {
        setHighlight(initHighlight);
    }, [initHighlight])

    return <Tag
        style={{display: 'inline-block'}}
        intent={highlight ? 'success' : 'none'}
        onMouseEnter={() => {
            setHighlight(true);
            onMouseEnter();
        }}
        onMouseLeave={() => {
            setHighlight(false);
            onMouseLeave();
        }}
        {...props}
    >
        {children}
    </Tag>
}

const TaxonomyBlock = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: auto;
    align-items: center;
`;

type Props = {
    thumbnail: ThumbnailWithLocationNameWithStatelems,
    showTaxonomies: boolean,
};

const ThumbnailWithStatelems = (props: Props) => {
    const {thumbnail, showTaxonomies = false, interactive = false} = props;
    const [lineTaxonomies, setLineTaxonomies] = useState([]);
    const [areaTaxonomies, setAreaTaxonomies] = useState([]);
    const [selectedAreaTaxonomy, setSelectedAreaTaxonomy] = useState(null);
    const [selectedLineTaxonomy, setSelectedLineTaxonomy] = useState(null);
    const [statelemsRenderer, setStatelemsRenderer] = useState(null);
    const [hoveredElem, setHoveredElem] = useState(null);
    const canvasRef = useRef(null);
    const taxonomiesRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        const canvas = canvasRef.current;
        const taxonomies = taxonomiesRef.current;
        if (!canvas) return;

        if (thumbnail) {
            if (!statelemsRenderer || statelemsRenderer.canvas !== canvas) {
                setStatelemsRenderer(new StatelemsRenderer(thumbnail, canvas, taxonomies, setHoveredElem));
                return;
            }

            (async () => {
                try {
                    if (cancelled) return;
                    await statelemsRenderer.renderImage({});
                    statelemsRenderer.renderAllDistinctAreaStatElems(
                        selectedAreaTaxonomy,
                    );
                    statelemsRenderer.renderAllDistinctLineStatElems(
                        selectedLineTaxonomy,
                    );
                    if (interactive) statelemsRenderer.addMouseOverEvents();
                    setLineTaxonomies(statelemsRenderer.getAllLineTaxonomies());
                    setAreaTaxonomies(statelemsRenderer.getAllAreaTaxonomies());
                } catch (err) {
                    if (!cancelled) throw err;
                }
            })();
        }

        return () => {
            cancelled = true;
            if (statelemsRenderer) {
                statelemsRenderer.unmount();
            }
        };
    }, [thumbnail, statelemsRenderer, canvasRef]);

    useEffect(() => {
        if (!!canvasRef.current && !!thumbnail && !!statelemsRenderer) {
            statelemsRenderer.renderImage({}).then(() => {
                statelemsRenderer.renderAllDistinctAreaStatElems(
                    selectedAreaTaxonomy,
                );
                statelemsRenderer.renderAllDistinctLineStatElems(
                    selectedLineTaxonomy,
                );
            });
        }
    }, [selectedAreaTaxonomy, selectedLineTaxonomy])

    return (
        <>
            {showTaxonomies && lineTaxonomies.length > 0 && (
                <>
                    <TaxonomyBlock>
                        <span>Lines:</span>
                        {lineTaxonomies.map((taxonomy, i) => (
                            <TaxonomyTag
                                key={i}
                                large
                                onMouseEnter={() => setSelectedLineTaxonomy(taxonomy)}
                                onMouseLeave={() => setSelectedLineTaxonomy(null)}
                                highlight={
                                    hoveredElem &&
                                    Object.getPrototypeOf(hoveredElem) === Curve.prototype &&
                                    hoveredElem.taxonomies.has(taxonomy)
                                }
                            >
                                {taxonomy}
                            </TaxonomyTag>
                        ))}
                    </TaxonomyBlock>
                    <br/>
                </>
            )}
            {showTaxonomies && areaTaxonomies.length > 0 && (
                <>
                    <TaxonomyBlock>
                        <span>Areas:</span>
                        {areaTaxonomies.map((taxonomy, i) => (
                            <TaxonomyTag
                                key={i}
                                large
                                onMouseEnter={() => setSelectedAreaTaxonomy(taxonomy)}
                                onMouseLeave={() => setSelectedAreaTaxonomy(null)}
                                highlight={
                                    hoveredElem &&
                                    Object.getPrototypeOf(hoveredElem) === Area.prototype &&
                                    hoveredElem.taxonomies.has(taxonomy)
                                }
                            >
                                {taxonomy}
                            </TaxonomyTag>
                        ))}
                    </TaxonomyBlock>
                    <br/>
                </>
            )}
            <div id="canvas-container" className="relative">
                <CameraCanvas ref={canvasRef} className="w-full h-auto"/>
                <Taxonomies ref={taxonomiesRef}></Taxonomies>
            </div>
        </>
    );
};

export default ThumbnailWithStatelems;
